import { InputField, Select } from '@salt/components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getProjects, getProjectUsers } from '../../../api/users';

const MemberPicker = ({
  project,
  selected,
  setSelected,
  setHasUpdated = () => {},
  description,
}) => {
  const [nonSelected, setNonSelected] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [filteredProject, setFilteredProject] = useState('all');
  const [userTotal, setUserTotal] = useState(0);
  const [unassignedUserCount, setUnassignedUserCount] = useState(0);

  const fetchUsers = useCallback(async () => {
    // setLoading(true);
    try {
      const { usersItems } = await getProjectUsers();
      const { projectItems, unassignedCount } = await getProjects({ limit: 1000 });

      setUserTotal(usersItems.length);
      setUnassignedUserCount(unassignedCount || 0);

      const notSelected = project
        ? usersItems.filter((u) => !project.members?.find((m) => m.id === u.id))
        : usersItems;
      const areSelected = project
        ? usersItems.filter((u) => project.members?.find((m) => m.id === u.id))
        : [];

      setNonSelected(notSelected);
      setSelected(areSelected);
      setProjects(projectItems);
    } catch (error) {
      console.error(error.message);
    }
    // setLoading(false);
  }, [project]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRemoveMember = useCallback(
    (userId) => {
      const user = selected?.find((u) => u.id === userId);
      setNonSelected([user, ...nonSelected]);
      setSelected(selected.filter((u) => u.id !== userId));
      setHasUpdated(true);
    },
    [selected, nonSelected]
  );

  const handleAddMember = useCallback(
    (userId) => {
      const user = nonSelected?.find((u) => u.id === userId);
      setSelected([...selected, user]);
      setNonSelected(nonSelected.filter((u) => u.id !== userId));
      setHasUpdated(true);
    },
    [selected, nonSelected]
  );

  const filteredSelected = useMemo(
    () =>
      selected.filter(
        (m) =>
          (userSearch.length === 0 || m.name.toLowerCase().includes(userSearch.toLowerCase())) &&
          (filteredProject === 'all' ||
            (filteredProject === 'unassigned'
              ? !m.project?.id
              : String(m.project?.id) === filteredProject))
      ),
    [selected, userSearch, filteredProject]
  );

  const filteredNonSelected = useMemo(
    () =>
      nonSelected.filter(
        (m) =>
          (userSearch.length === 0 || m.name.toLowerCase().includes(userSearch.toLowerCase())) &&
          (filteredProject === 'all' ||
            (filteredProject === 'unassigned'
              ? !m.project?.id
              : String(m.project?.id) === filteredProject))
      ),
    [nonSelected, userSearch, filteredProject]
  );

  const projectValue = useMemo(() => {
    const proj = projects?.find((p) => String(p.id) === filteredProject);
    if (proj) return proj.name;
    if (filteredProject === 'unassigned') return 'Unassigned Users';
    return 'All Users';
  }, [projects, filteredProject]);

  console.log('TEST1.1', filteredNonSelected);

  return (
    <div>
      <div className="text-base">{description}</div>
      <div className="my-4 flex">
        <div className="search w-[318px]">
          <InputField
            icon="search"
            iconPlacement="left"
            onChange={(event) => setUserSearch(event.target.value)}
            onClear={() => setUserSearch('')}
            placeholder="Search for User"
            value={userSearch}
            labelBackgroundClass="peer-focus:bg-secondary peer-[:not(:placeholder-shown)]:bg-secondary"
            backgroundClass="bg-secondary-light"
          />
        </div>
        <div className="mx-4 mr-auto">
          <Select
            icon="filter"
            onClick={(newVal) => setFilteredProject(newVal)}
            width="w-[200px]"
            labelBackgroundClass="group-focus:bg-secondary"
            backgroundClass="bg-secondary-light"
            permScroller={projects?.length > 2}
            renderItems={[
              {
                id: 'all',
                render: () => {},
                value: `All Users (${userTotal})`,
              },
              {
                id: 'unassigned',
                render: () => {},
                value: `Unassigned Users (${unassignedUserCount})`,
              },
              ...projects.map((p) => ({
                id: String(p.id),
                value: `${p.name} (${p.members_count || 0})`,
                render: () => {},
              })),
            ]}
            value={projectValue}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 w-full mb-2">
        <div>
          <span className="text-[#434C58]/60">Available Users ({filteredNonSelected.length})</span>
          <div className="h-[256px] overflow-scroll border-b border-t border-l border-solid border-[#CCCCCC] mt-2">
            {filteredNonSelected.map((u) => {
              const uProject = projects?.find((p) => p.id === u.project?.id);
              return (
                <div
                  key={u.id}
                  className="flex border-b border-solid border-[#CCCCCC] px-4 py-2 items-center h-14"
                >
                  <div>
                    <span className="text-base">{u.name}</span>
                    {uProject && uProject?.id !== project?.id && (
                      <div
                        style={{
                          backgroundColor: `${uProject?.accent_colour}40`,
                          color: uProject?.accent_colour,
                        }}
                        className="text-[10px] rounded-full py-0.5 px-2 w-fit"
                      >
                        {uProject?.name}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddMember(u.id)}
                    className="flex items-center justify-center rounded-full w-8 h-8 bg-[#37C2D7]/10 ml-auto"
                  >
                    {uProject && uProject.id !== project?.id ? (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 5C10 4.5407 9.70556 4.26744 9.66111 4.22674L5.90556 0.296512C5.52778 -0.0988372 4.91111 -0.0988372 4.53333 0.296512C4.15556 0.69186 4.15556 1.33721 4.53333 1.73256L6.68333 3.98256L0.972222 3.98256C0.433333 3.98256 0 4.43605 0 5C0 5.56395 0.433333 6.01744 0.972222 6.01744L6.67222 6.01744L4.53333 8.25581C4.15556 8.65116 4.15556 9.29651 4.53333 9.69186C4.91111 10.0872 5.52778 10.0872 5.90556 9.69186L9.65556 5.76744C9.7 5.72674 10 5.45349 10 4.99419V5Z"
                          fill="#37C2D7"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_948_2623)">
                          <path
                            d="M1.11351 3.8892C0.498537 3.8892 0 4.38713 0 5.00136C0 5.61559 0.498537 6.11353 1.11351 6.11353L8.88649 6.11353C9.50146 6.11353 10 5.61559 10 5.00136C10 4.38713 9.50146 3.8892 8.88649 3.8892L1.11351 3.8892Z"
                            fill="#00CD8F"
                          />
                          <path
                            d="M3.88896 8.88649C3.88896 9.50146 4.38689 10 5.00112 10C5.61535 10 6.11328 9.50146 6.11328 8.88649L6.11328 1.11351C6.11328 0.498537 5.61535 0 5.00112 0C4.38689 0 3.88896 0.498537 3.88896 1.11351L3.88896 8.88649Z"
                            fill="#00CD8F"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_948_2623">
                            <rect width="10" height="10" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <span className="text-[#434C58]/60">Selected Users ({filteredSelected.length})</span>
          <div className="h-[256px] overflow-scroll border border-solid border-[#CCCCCC] mt-2">
            {filteredSelected.map((u) => {
              return (
                <div
                  key={u.id}
                  className="flex border-b border-solid border-[#CCCCCC] px-4 py-2 items-center h-14"
                >
                  <div>
                    <span className="text-base">{u.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(u.id)}
                    className="flex items-center justify-center rounded-full w-8 h-8 bg-[#FF3366]/10 ml-auto"
                  >
                    <svg
                      width="10"
                      height="3"
                      viewBox="0 0 10 3"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.11351 4.07696e-05C0.498537 4.07696e-05 0 0.497973 0 1.1122C0 1.72643 0.498537 2.22437 1.11351 2.22437L8.88649 2.22437C9.50146 2.22437 10 1.72643 10 1.1122C10 0.497973 9.50146 4.07696e-05 8.88649 4.07696e-05L1.11351 4.07696e-05Z"
                        fill="#FF3366"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
export default MemberPicker;
